import { motion } from "framer-motion";
import { APP_LOGO } from "@/const";
import { Github, Linkedin } from "lucide-react";
import { Link } from "wouter";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    Services: [
      { label: "Custom Websites", href: "/services/websites" },
      { label: "Mobile Apps", href: "/services/mobile-apps" },
      { label: "AI-Powered Tools", href: "/services/ai-tools" },
      { label: "Marketing Automation", href: "/services/automation" },
      { label: "Business Analytics", href: "/services/analytics" },
      { label: "Custom Solutions", href: "/services/custom-solutions" },
    ],
    Company: [
      { label: "About Us", href: "/#about" },
      { label: "Our Process", href: "/#process" },
      { label: "Case Studies", href: "/case-studies" },
      { label: "Support", href: "/support" },
    ],
    Resources: [
      { label: "Blog", href: "/blog" },
      { label: "FAQ", href: "/faq" },
      { label: "Contact", href: "/#contact" },
    ],
  };

  const socialLinks = [
    {
      icon: Linkedin,
      href: "https://www.linkedin.com/company/mosheli-creative-solutions",
      label: "LinkedIn",
    },
    {
      icon: Github,
      href: "https://github.com/mosheli-sudo",
      label: "GitHub",
    },
  ];

  return (
    <footer className="bg-[oklch(0.25_0.02_210)] text-white">
      <div className="container py-16 md:py-20">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img src={APP_LOGO} alt="Mosheli" width="144" height="48" className="h-12 w-auto mb-6" />
              <p className="text-white/70 leading-relaxed mb-6 max-w-md">
                Empowering small businesses with enterprise-grade custom solutions. From websites to
                mobile apps, we build technology that drives growth.
              </p>
              {/* Social Links */}
              <div className="flex gap-4">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    aria-label={social.label}
                    target="_blank"
                    rel="noreferrer"
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([category, links], categoryIndex) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
            >
              <h3 className="font-bold text-lg mb-4">{category}</h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith('/#') ? (
                      <a
                        href={link.href}
                        className="text-white/70 hover:text-white transition-colors inline-block hover:translate-x-1 duration-200"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link href={link.href}>
                        <a className="text-white/70 hover:text-white transition-colors inline-block hover:translate-x-1 duration-200">
                          {link.label}
                        </a>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Bottom Bar */}
        <motion.div
          className="pt-8 border-t border-white/10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/60 text-sm">
              © {currentYear} Mosheli Creative Solutions LLC. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link href="/privacy-policy">
                <a className="text-white/60 hover:text-white transition-colors">Privacy Policy</a>
              </Link>
              <Link href="/terms-conditions">
                <a className="text-white/60 hover:text-white transition-colors">Terms of Service</a>
              </Link>
              <Link href="/cookie-policy">
                <a className="text-white/60 hover:text-white transition-colors">Cookie Policy</a>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
